import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  DataTableAddRow,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  PanelOverlay,
  Separator,
  Text,
  type BulkAction,
  type EntitySelection,
} from "@salestrak/ui";

import { PageHeader, PageHeaderActions, PageHeaderTitle } from "#app/components/page-header";
import { CustomFieldsTable } from "#app/components/custom-fields/custom-fields-table";
import {
  CustomFieldFormDialog,
  type CustomFieldFormMode,
} from "#app/components/custom-fields/custom-field-form-dialog";
import {
  useFieldTypeSelectorConfig,
  FIELD_TYPE_POOL_ID,
} from "#app/lib/hooks/use-field-type-order";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { CUSTOM_FIELD_LIST } from "#app/lib/filters/list-configs";
import type { CustomFieldRow, CustomFieldType } from "#app/lib/data";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { useCustomFieldMutations } from "#app/lib/hooks/use-custom-field-mutations";
import { usePermission } from "#app/lib/hooks/use-permission";
import { buildCustomFieldBulkActions } from "#app/components/custom-fields/custom-field-bulk-actions";
import { withPermission } from "#app/components/permissions/require-permission";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function CustomFieldsPage() {
  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    where,
    orderBy,
  } = useListQueryState(CUSTOM_FIELD_LIST);

  const { createCustomField, updateCustomField, deleteCustomField, duplicateCustomField, refetchList } =
    useCustomFieldMutations();

  // --- Create field type selector ---
  const fieldTypeSelectorConfig = useFieldTypeSelectorConfig();

  const handleFieldTypeSelect = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (selected) {
        setFormMode({ mode: "create", type: selected.id as CustomFieldType });
      }
    },
    [],
  );

  // "+ Create field" as a real row at the bottom of the list (the reference
  // add-row grammar) — the same field-type picker as the header button,
  // anchored to the row. The table drops it while the list is empty.
  const renderGroupFooter = useCallback(
    () => (
      <EntitySelector
        title="Field type"
        config={fieldTypeSelectorConfig}
        value={[]}
        onValueChange={handleFieldTypeSelect}
      >
        <EntitySelectorTrigger render={<DataTableAddRow label="Create field" />} />
        <EntitySelectorContent>
          <EntitySelectorInput />
          <EntitySelectorPool value={FIELD_TYPE_POOL_ID} />
          <EntitySelectorEmpty>No matching field types</EntitySelectorEmpty>
        </EntitySelectorContent>
      </EntitySelector>
    ),
    [fieldTypeSelectorConfig, handleFieldTypeSelect],
  );

  // --- Form dialog state ---
  const [formMode, setFormMode] = useState<CustomFieldFormMode | null>(null);

  const handleEdit = useCallback((field: CustomFieldRow) => {
    setFormMode({ mode: "edit", field });
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormMode(null);
  }, []);

  // --- Delete dialog ---
  // Registry interlock: custom_fields.manage reached this page, but deleting
  // a field destroys its stored values on every item, so it additionally
  // needs an unconditional Delete Items grant — without it every delete
  // affordance (row menu, bulk bar, form danger zone) disappears.
  const canDelete = usePermission("delete.items");
  const [deleteTarget, setDeleteTarget] = useState<CustomFieldDef | null>(null);

  const handleDelete = useCallback((field: CustomFieldDef) => {
    setDeleteTarget(field);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteCustomField(deleteTarget.id);
    await refetchList();
    setFormMode(null);
    setDeleteTarget(null);
  }, [deleteTarget, deleteCustomField, refetchList]);

  // --- Duplicate ---
  const handleDuplicate = useCallback(
    async (field: CustomFieldRow) => {
      await duplicateCustomField(field.id);
    },
    [duplicateCustomField],
  );

  // --- Bulk actions ---
  const bulkActions = useMemo<BulkAction<CustomFieldRow>[]>(
    () =>
      buildCustomFieldBulkActions({
        canDelete,
        deleteCustomField,
        duplicateCustomField,
        refetchList,
      }),
    [canDelete, deleteCustomField, duplicateCustomField, refetchList],
  );

  return (
    // PanelOverlay anchors to this container, and a plain Box is
    // `position: static`, so the relative position is explicit.
    <Box direction="column" fill="flex" position="relative">
      <PageHeader centered>
        <PageHeaderTitle size="sm">Custom Fields</PageHeaderTitle>
        <PageHeaderActions>
          <EntitySelector
            title="Field type"
            config={fieldTypeSelectorConfig}
            value={[]}
            onValueChange={handleFieldTypeSelect}
          >
            <EntitySelectorTrigger
              render={
                <Button size="sm">
                  <Icon name="plus" />
                  <Text>Create field</Text>
                </Button>
              }
            />
            <EntitySelectorContent>
              <EntitySelectorInput />
              <EntitySelectorPool value={FIELD_TYPE_POOL_ID} />
              <EntitySelectorEmpty>No matching field types</EntitySelectorEmpty>
            </EntitySelectorContent>
          </EntitySelector>
        </PageHeaderActions>
      </PageHeader>

      {/* The divider between the page header and the table's toolbar — the
          table itself draws no top rule (the manage dialog's bordered header
          already supplies one there). */}
      <Separator />
      <CustomFieldsTable
        where={where ?? {}}
        orderBy={orderBy}
        groupByField="none"
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={canDelete ? handleDelete : undefined}
        bulkActions={bulkActions}
        groupFooter={renderGroupFooter}
      />

      {/* Create / Edit sidebar panel — the same right-edge sunken slide-over
          the manage dialog presents, instead of the form rendering inline
          below the table. */}
      {formMode ? (
        <PanelOverlay surface="sunken" onDismiss={handleCloseForm}>
          <CustomFieldFormDialog
            formMode={formMode}
            onClose={handleCloseForm}
            onCreate={createCustomField}
            onUpdate={updateCustomField}
            onDelete={canDelete ? handleDelete : undefined}
          />
        </PanelOverlay>
      ) : null}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the {deleteTarget?.name} field? It will
              no longer be available to use in any issues or forms. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              <Text>Cancel</Text>
            </AlertDialogClose>
            <Button onClick={confirmDelete}>
              <Text>Delete field</Text>
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/custom-fields")({
  component: withPermission("custom_fields.manage", CustomFieldsPage),
});
